import React, { useState, useEffect, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useOKREllipsisValues } from 'Deer/intent/Goals/hooks/useOKREllipsisValues';
import { useSelector } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';
import { okrsPageSelector } from 'selectors/okrsPage';
import { checkIntentLandingPagesFeatureEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { AgGridSearchBar } from 'uiToolkitV1/AgGridSearchBar';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

import { useGenerateGoalsDataExportsReport } from './hooks/dataHooks';

// this should not be there in api response at all, but are there for some technical reason.
// if removed from BE, this filter can also be removed.
// discuss with Arun for details
export const filterNotRequiredColumns = (col) =>
  ![
    'EMAIL',
    'PERSONAL_EMAIL',
    'WORK_EMAIL',
    'MANAGER_EMAIL',
    'MANAGER_WORK_EMAIL',
    'MANAGER_PERSONAL_EMAIL',
    'PROFILE_PHOTO',
  ].includes(col.columnId);

function GoalsDataExportsReportGrid({ gridRef, gridStatePersistenceKey }) {
  const currentCompRef = useRef();
  const agGridRef = gridRef ?? currentCompRef;
  const cohortDetails = useSelectedView();
  const { selectedTimelines } = useSelector(okrsPageSelector);

  const timeline = selectedTimelines?.[0];
  const [columnDefs, setColumnDefs] = useState([]);

  const { isFetching, data: goalsDataExportsReportsData } = useGenerateGoalsDataExportsReport({
    cohortId: cohortDetails?.id,
    timelineTag: timeline?.timelineTag,
  });
  const { columns, rows = [] } = goalsDataExportsReportsData ?? {};
  const selectedIntent = { id: 'DATA_EXPORTS' };

  const ellipsisMenuOptions = useOKREllipsisValues({
    gridRef,
    selectedIntent,
  });

  useEffect(() => {
    if (!columns || columns.length === 0) return;
    const newColumnDefs = columns.filter(filterNotRequiredColumns).map((col) => {
      const colProps = col;
      return {
        colId: colProps.columnId,
        field: colProps.displayName,
        id: colProps.columnId,
        displayName: colProps.displayName,
        description: colProps.description,
        minWidth: 220,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
          displayName: colProps.displayName,
        },

        ...getColumnDefinitionByType(colProps),
      };
    });

    setColumnDefs(newColumnDefs);
  }, [columns]);

  useEffect(() => {
    if (isFetching) {
      agGridRef.current?.api?.setGridOption('loading', true);
    } else {
      agGridRef.current?.api?.setGridOption('loading', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agGridRef.current, isFetching]);

  return (
    <PaperWithShadow p={4} mb={1} mt={checkNewNavEnabled() ? 0 : 3} position="relative">
      <Flex alignItems="center" justifyContent="space-between" pb={2}>
        <Box w="300px">
          <AgGridSearchBar gridRef={agGridRef} />
        </Box>

        {checkIntentLandingPagesFeatureEnabled() === false && (
          <Flex alignItems="center">
            <AgGridEllipsisMenu options={ellipsisMenuOptions} />
          </Flex>
        )}
      </Flex>
      <Box className="goals-data-exports-report-grid">
        <AgGridTable
          key={gridStatePersistenceKey} // we want to re-initialize on key change
          gridStatePersistenceKey={gridStatePersistenceKey}
          ref={agGridRef}
          rowData={rows}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          pagination
          paginationPageSize={10}
          headerHeight={36}
          rowHeight={56}
          kebabMenuIsRowSpecific={false}
          containerAutoHeight
          rowStyle={{ cursor: 'default' }}
          defaultColDef={{ cellStyle: { display: 'flex', alignItems: 'center' } }}
          loading={isFetching}
        />
      </Box>
    </PaperWithShadow>
  );
}

export { GoalsDataExportsReportGrid };
